import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { Icon, SidebarPortal, BlockDataForm } from '@plone/volto/components';
import { Segment, Message, List, Radio } from 'semantic-ui-react';
import uploadSVG from '@plone/volto/icons/upload.svg';
import config from '@plone/volto/registry';
import { useIntl } from 'react-intl';
import Schema from './Schema';

function parseApplistTxt(text) {
  // Parser for applist.txt that returns a flat object keyed by app id
  // Desired shape:
  // {
  //   "activity": { desc: "", updated: false, added: false, version: "4.0.0" },
  //   ...
  // }
  const lines = text.split(/\r?\n/);
  const result = {};

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    // match lines like: - activity: 4.0.0
    const m = line.match(/^-\s+([^:]+):\s+(.+)$/);
    if (m) {
      const id = m[1].trim();
      const version = m[2].trim();
      result[id] = { desc: '', updated: false, added: false, version };
    }
  }
  return result;
}

const Edit = (props) => {
  const { data, block, className, onChangeBlock, selected } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const apiBase = config.settings?.apiPath;

  const targetPath = useMemo(() => {
    const userTarget = (data?.target_path || '').trim();
    if (userTarget.startsWith('http')) return userTarget; // allow full API URL if provided
    if (userTarget.startsWith('/')) return userTarget; // absolute Plone path
    // default to root /applist for empty or relative values
    return '/applist';
  }, [data?.target_path]);

  // Load existing files from the applist folder
  useEffect(() => {
    let aborted = false;
    async function load() {
      try {
        if (!apiBase) return;
        const url = `${apiBase}/++api++/@applist-upload?target_path=${encodeURIComponent(
          targetPath,
        )}`;
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!aborted) {
          const list = json?.items || [];
          setRemoteItems(list);
          // Do not override existing selection; only help if none is selected and there is exactly one option
          if (!data?.selected_file && list.length === 1) {
            onSelect(list[0]);
          }
        }
      } catch (e) {
        if (!aborted) setError(e.message || String(e));
      }
    }
    setError(null);
    load();
    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPath, apiBase]);

  const [remoteItems, setRemoteItems] = useState([]);
  const items = remoteItems;

  const onSelect = (item) => {
    const href = item['@id'];
    const baseId = href ? href.split('/').pop() : undefined;
    const id = item.id || baseId;
    const title = item.title || id || href;

    onChangeBlock(block, {
      ...data,
      selected_file: { '@id': href, id, title },
    });
  };

  const onFile = useCallback(
    async (ev) => {
      setStatus(null);
      setError(null);
      const file = ev.target.files?.[0];
      if (!file) return;
      const text = await file.text();

      try {
        const form = new FormData();
        const txt = new Blob([text], { type: 'text/plain' });
        form.append('file', txt, 'applist.txt');
        form.append('target_path', targetPath);
        const res = await fetch(`${apiBase}/++api++/@applist-upload`, {
          method: 'POST',
          body: form,
        });
        if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`);
        const json = await res.json();
        setStatus(`Gespeichert: ${json?.saved || ''}`);
        const newItems = json?.items || [];
        setRemoteItems(newItems);
        // Auto-select the freshly uploaded file
        const savedUrl = json?.saved;
        if (savedUrl) {
          const found = newItems.find((it) => it['@id'] === savedUrl);
          if (found) {
            onSelect(found);
          } else {
            // Fallback: construct a minimal item
            const baseId = savedUrl.split('/').pop();
            onSelect({ '@id': savedUrl, id: baseId, title: baseId });
          }
        }
      } catch (e) {
        setError(e.message || String(e));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetPath, dispatch, block],
  );

  return (
    <>
      <Segment className={cx('block', 'applistupload', className)}>
        {(data?.title || data?.description) && (
          <div style={{ marginBottom: 12 }}>
            {data?.title && <h3 style={{ margin: '0 0 4px' }}>{data.title}</h3>}
            {data?.description && (
              <p style={{ margin: 0, color: 'rgba(0,0,0,0.6)' }}>
                {data.description}
              </p>
            )}
          </div>
        )}
        <div>
          <p>Vorhandene Dateien im Ordner: {targetPath}</p>
          {items.length > 0 ? (
            <List selection verticalAlign="middle">
              {items.map((item) => (
                <List.Item key={item['@id']} onClick={() => onSelect(item)}>
                  <List.Content floated="right">
                    <Radio
                      checked={data?.selected_file?.['@id'] === item['@id']}
                      onChange={() => onSelect(item)}
                    />
                  </List.Content>
                  <List.Content>
                    <List.Header>{item.title || item.id}</List.Header>
                    <List.Description>{item['@id']}</List.Description>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          ) : (
            <Message info content="Keine Dateien im Ordner gefunden." />
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 12,
          }}
        >
          <label className="ui button primary" htmlFor={`file-${block}`}>
            <Icon name={uploadSVG} size="18px" /> Upload applist.txt
          </label>
          <input
            id={`file-${block}`}
            type="file"
            accept=".txt"
            onChange={onFile}
            style={{ display: 'none' }}
          />
        </div>
        {status && <Message positive content={status} />}
        {error && <Message negative content={error} />}
      </Segment>
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={Schema({ intl })}
          title={Schema({ intl }).title}
          onChangeField={(id, value) =>
            onChangeBlock(block, {
              ...data,
              [id]: value,
            })
          }
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

export default Edit;
export { parseApplistTxt };
